import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Calendar, IndianRupee, CreditCard, Smartphone, Building2,
  TrendingUp, Clock, Package, X, Download, RefreshCw
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';

const POSDailyReportPage = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [closedReports, setClosedReports] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  const { isOwner, canEdit } = usePermissions();
  const canCloseDay = isOwner || canEdit('pos');

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchDailyReport();
      fetchClosedReports();
    }
  }, [bid, selectedDate]);

  const fetchDailyReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/pos/daily-report', { params: { date: selectedDate } });
      setReport(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  };

  const fetchClosedReports = async () => {
    try {
      const { data } = await api.get('/pos/daily-reports');
      setClosedReports(data.reports || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load closed reports'));
    }
  };

  const handleCloseDay = () => {
    setConfirmModal({
      title: 'Close Day',
      description: 'Are you sure you want to close the day? This action cannot be undone.',
      confirmLabel: 'Close Day',
      confirmClass: 'bg-slate-800 hover:bg-slate-700',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.post('/pos/daily-report/close', null, { params: { date: selectedDate } });
          toast.success('Day closed successfully');
          fetchDailyReport();
          fetchClosedReports();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error closing day'));
        }
      }
    });
  };

  const paymentModeIcons = {
    CASH: IndianRupee,
    UPI: Smartphone,
    CARD: CreditCard,
    BANK: Building2
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/business/${bid}/pos`)} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Daily Sales Report</h1>
            <p className="text-sm text-slate-500">View daily sales summary</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          />
          <button onClick={fetchDailyReport} className="p-2 hover:bg-slate-100 rounded-lg">
            <RefreshCw className="w-5 h-5 text-slate-600" />
          </button>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            {showHistory ? 'Current' : 'History'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : showHistory ? (
        /* Closed Reports History */
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Closed Reports</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {closedReports.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No closed reports yet</div>
            ) : (
              closedReports.map((r, i) => (
                <div key={i} className="p-4 hover:bg-slate-50 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-800">{r.report_date || r.date}</p>
                    <p className="text-sm text-slate-500">{r.total_transactions || 0} invoices</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-emerald-600">₹{((r.total_sales || 0) - (r.total_refunds || 0)).toLocaleString()}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        /* Current Day Report */
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-slate-500">Total Invoices</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">{report?.summary?.total_invoices || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <span className="text-xs text-slate-500">Gross Sales</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">₹{(report?.summary?.gross_sales || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <X className="w-4 h-4 text-red-500" />
                <span className="text-xs text-slate-500">Refunds</span>
              </div>
              <p className="text-2xl font-bold text-red-600">₹{(report?.summary?.total_refund_amount || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-slate-500">Net Sales</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">₹{(report?.summary?.net_sales || 0).toLocaleString()}</p>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-4">Payment Breakdown</h3>
              <div className="space-y-3">
                {report?.payment_breakdown && Object.entries(report.payment_breakdown).map(([mode, amount]) => {
                  const Icon = paymentModeIcons[mode] || IndianRupee;
                  return (
                    <div key={mode} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-slate-600" />
                        <span className="font-medium text-slate-700">{mode}</span>
                      </div>
                      <span className="font-semibold text-slate-800">₹{amount.toLocaleString()}</span>
                    </div>
                  );
                })}
                {(!report?.payment_breakdown || Object.keys(report.payment_breakdown).length === 0) && (
                  <p className="text-center text-slate-500 py-4">No payments today</p>
                )}
              </div>
            </div>

            {/* Top Items */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-4">Top Selling Items</h3>
              <div className="space-y-3">
                {report?.top_selling_items?.slice(0, 5).map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-700">{item.item_name}</p>
                      <p className="text-xs text-slate-500">{item.quantity_sold} sold</p>
                    </div>
                    <span className="font-semibold text-slate-800">₹{item.total_amount.toLocaleString()}</span>
                  </div>
                ))}
                {(!report?.top_selling_items || report.top_selling_items.length === 0) && (
                  <p className="text-center text-slate-500 py-4">No items sold today</p>
                )}
              </div>
            </div>
          </div>

          {/* Hourly Sales */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-800 mb-4">Hourly Sales</h3>
            <div className="grid grid-cols-8 lg:grid-cols-12 gap-1">
              {Array.from({ length: 24 }, (_, i) => i).map(hour => {
                const hourlySalesObj = report?.hourly_sales || {};
                const hourKey = `${String(hour).padStart(2, '0')}:00`;
                const hourData = hourlySalesObj[hourKey];
                const allAmounts = Object.values(hourlySalesObj).map(v => v?.amount || 0);
                const maxSales = Math.max(...allAmounts, 1);
                const hourAmount = hourData?.amount || 0;
                const height = hourAmount ? Math.max(20, (hourAmount / maxSales) * 100) : 20;
                return (
                  <div key={hour} className="text-center">
                    <div className="h-24 flex items-end justify-center">
                      <div
                        className="w-full bg-blue-500 rounded-t transition-all"
                        style={{ height: `${height}%` }}
                        title={`₹${hourAmount}`}
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">{hour}:00</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Close Day Button */}
          {canCloseDay && selectedDate === new Date().toISOString().split('T')[0] && !report?.is_closed && !closedReports.some(r => (r.report_date || r.date) === selectedDate) && (
            <div className="flex justify-end">
              <button
                onClick={handleCloseDay}
                className="px-6 py-3 bg-slate-800 text-white font-medium rounded-lg hover:bg-slate-700"
              >
                Close Day
              </button>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default POSDailyReportPage;
